import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "title", "open", "tabIndex", "onChange", "maxHeight", "id"];
import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { ChevronDown as Arrow } from '@resultadosdigitais/tangram-react-icons';
import { useId } from '../hooks';
import ScopedCssBaseline from '../StyleBase/ScopedCssBaseline';
import useKeyboardNavigation from '../hooks/useKeyboardNavigation';
import AccordionGroup from './AccordionGroup';
import Collapse from '../Collapse';
import AccordionTitle from './AccordionTitle';
const Root = styled.div.withConfig({
  displayName: "Accordion__Root",
  componentId: "sc-13872g3-0"
})(["", " width:100%;background-color:var(--neutral-surface);"], ScopedCssBaseline);
const WrapperTitle = styled.div.withConfig({
  displayName: "Accordion__WrapperTitle",
  componentId: "sc-13872g3-1"
})(["display:flex;gap:var(--size-spacing-02);align-items:center;flex:1;margin:0 var(--size-spacing-04) 0 0;font:inherit;color:inherit;> svg{--custom-icon-color:var(--neutral-icon-low-emphasis);}"]);
const Head = styled.div.withConfig({
  displayName: "Accordion__Head",
  componentId: "sc-13872g3-2"
})(["align-items:center;background-color:var(--neutral-surface);cursor:pointer;display:flex;outline:none;font:normal var(--text-md-bold);padding:var(--size-spacing-03) 0;&:focus ", ",&:hover ", "{color:var(--primary-text);}& > svg:last-child{will-change:transform;transition-duration:var(--motion-duration-normal);transition-timing-function:var(--motion-timing-function-ease-in-out);transition-property:transform;fill:var(--neutral-icon-low-emphasis);", "}"], WrapperTitle, WrapperTitle, _ref => {
  let {
    open
  } = _ref;
  return open && css(["transform:rotate(180deg);"]);
});
const Body = styled(Collapse).withConfig({
  displayName: "Accordion__Body",
  componentId: "sc-13872g3-3"
})([""]);
const Content = styled.div.withConfig({
  displayName: "Accordion__Content",
  componentId: "sc-13872g3-4"
})([""]);
const Accordion = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
      children,
      title,
      open: openProp,
      tabIndex,
      onChange,
      maxHeight,
      id: idProp
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const id = useId(idProp);
  const [stateOpen, setOpen] = useState(false);
  const headId = "head-".concat(id);
  const contentId = "content-".concat(id);
  const {
    keyboardNavigationProps
  } = useKeyboardNavigation();
  const isControlled = openProp != null;
  const open = isControlled ? openProp : stateOpen;
  const handleChange = event => {
    const newState = !open;
    if (!isControlled) {
      setOpen(newState);
    }
    onChange === null || onChange === void 0 ? void 0 : onChange(event, newState);
  };
  const handleKeyDown = event => {
    if (event.key === ' ' || event.key === 'Enter') handleChange(event);
  };
  const renderTitle = () => {
    return typeof title == 'function' ? title() : title;
  };
  return /*#__PURE__*/React.createElement(Root, _extends({}, rest, {
    id: id,
    ref: ref
  }), /*#__PURE__*/React.createElement(Head, _extends({
    "aria-controls": contentId,
    "aria-expanded": open,
    id: headId,
    onClick: event => handleChange(event),
    onKeyDown: event => handleKeyDown(event),
    open: open,
    role: "button",
    tabIndex: tabIndex || 0
  }, keyboardNavigationProps), /*#__PURE__*/React.createElement(WrapperTitle, null, renderTitle()), /*#__PURE__*/React.createElement(Arrow, {
    fill: "var(--neutral-icon-low-emphasis)",
    titleId: "arrowId",
    title: open ? 'Hide Icon' : 'Show Icon'
  })), /*#__PURE__*/React.createElement(Body, {
    collapsed: !open,
    role: "region",
    id: contentId,
    "aria-labelledby": headId,
    tabIndex: tabIndex,
    maxHeight: maxHeight
  }, /*#__PURE__*/React.createElement(Content, null, children)));
});
Accordion.displayName = 'Accordion';
Accordion.Style = Root;
Accordion.HeadStyle = Head;
Accordion.BodyStyle = Body;
Accordion.Group = AccordionGroup;
Accordion.Title = AccordionTitle;
process.env.NODE_ENV !== "production" ? Accordion.propTypes = {
  /**
   * Sets the children.
   */
  children: PropTypes.node,
  /**
   * Sets the title for the `Accordion`.
   *
   * @example
   * // Can be a string:
   * title="Title of my accordion"
   *
   * @example
   * // Can be a function:
   * title={() => <Text as="h3" token={Text.tokens.TEXT_MD_BOLD}>Title of my accordion</Text>}
   *
   * @example
   * // Or a node:
   * title={
   *  <>
   *    Title of my accordion
   *    <Tag kind={Tag.kinds.primary}>My label</Tag>
   *  </>
   * }
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]),
  /**
   * A boolean value to control if render opened or closed.
   */
  open: PropTypes.bool,
  /**
   * Sets the max height of the container.
   * If a number, the value will be automatically transformed into pixels.
   * Unlimit if not set.
   */
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Callback fired when the `Accordion` changes.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   * @param {boolean|number} newStateOrIndex - When `Accordion` is trigged inside of the `Accordion.Group`
   * it is the current index of the `Accordion`. Otherwise, it is a boolean about the open state.
   */
  onChange: PropTypes.func
} : void 0;
export default Accordion;